import { useEffect, useMemo, useState } from "react";
import { Inbox, Mail, MessageSquare, Zap, Globe } from "lucide-react";
import { NEED_OPTIONS } from "../config";

const API = process.env.REACT_APP_BACKEND_URL;

export const INQUIRY_STATUS_META = {
  new: { label: "New", color: "#8FBFE8" },
  contacted: { label: "Contacted", color: "#E8CE8F" },
  proposal_sent: { label: "Proposal sent", color: "#B8A7E8" },
  converted: { label: "Converted", color: "#8FD8B8" },
  archived: { label: "Archived", color: "rgba(246,243,252,0.4)" },
};

const chipBase =
  "rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac";

export function InquiriesPanel({ onFollowUp }) {
  const [inquiries, setInquiries] = useState(null);
  const [needFilter, setNeedFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openThreads, setOpenThreads] = useState(() => new Set());
  const [replyDrafts, setReplyDrafts] = useState({});
  const [replyBusy, setReplyBusy] = useState(null);

  const logReply = async (inq) => {
    const note = (replyDrafts[inq.id] || "").trim();
    if (!note) return;
    setReplyBusy(inq.id);
    try {
      const res = await fetch(`${API}/api/admin/inquiries/${inq.id}/replies`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ note }),
      });
      if (res.ok) {
        const saved = await res.json();
        setInquiries((prev) =>
          prev.map((i) =>
            i.id === inq.id ? { ...i, thread: [{ ...saved, kind: "reply" }, ...(i.thread || [])] } : i
          )
        );
        setReplyDrafts((d) => ({ ...d, [inq.id]: "" }));
      }
    } finally {
      setReplyBusy(null);
    }
  };

  const toggleThread = (id) =>
    setOpenThreads((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/inquiries`, { credentials: "include" });
        setInquiries(res.ok ? await res.json() : []);
      } catch {
        setInquiries([]);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!inquiries) return null;
    return inquiries.filter(
      (i) =>
        (needFilter === "all" || i.need === needFilter) &&
        (statusFilter === "all" || (i.status || "new") === statusFilter)
    );
  }, [inquiries, needFilter, statusFilter]);

  const setStatus = async (inq, status) => {
    const res = await fetch(`${API}/api/admin/inquiries/${inq.id}/status`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ status }),
    });
    if (res.ok) {
      const updated = await res.json();
      setInquiries((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
    }
  };

  return (
    <div data-testid="admin-inquiries-panel">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="inquiry-filter-all"
          onClick={() => setNeedFilter("all")}
          className={`${chipBase} ${needFilter === "all" ? "border-lilac/60 bg-lilac/15 text-lilac" : "border-white/10 text-paper/50 hover:text-paper"}`}
        >
          All
        </button>
        {NEED_OPTIONS.map((need) => (
          <button
            key={need}
            type="button"
            data-testid={`inquiry-filter-${need.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            onClick={() => setNeedFilter(need)}
            className={`${chipBase} ${needFilter === need ? "border-lilac/60 bg-lilac/15 text-lilac" : "border-white/10 text-paper/50 hover:text-paper"}`}
          >
            {need}
          </button>
        ))}
        <select
          data-testid="inquiry-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filter by pipeline status"
          className="ml-auto rounded-full border border-white/10 bg-ink/70 px-3.5 py-1.5 text-xs font-bold text-paper/70 focus:border-lilac/60 focus:outline-none"
        >
          <option value="all">Every status</option>
          {Object.entries(INQUIRY_STATUS_META).map(([key, meta]) => (
            <option key={key} value={key}>{meta.label}</option>
          ))}
        </select>
      </div>

      {filtered === null ? (
        <p className="mt-8 text-sm text-paper/45">Loading…</p>
      ) : filtered.length === 0 ? (
        <div data-testid="admin-inquiries-empty" className="mt-8 rounded-3xl border border-white/10 bg-card p-12 text-center">
          <Inbox className="mx-auto h-9 w-9 text-paper/30" aria-hidden="true" />
          <p className="mt-4 text-sm text-paper/50">
            {inquiries.length === 0 ? "No inquiries yet. They'll land here as they arrive." : "Nothing matches those filters."}
          </p>
        </div>
      ) : (
        <ul data-testid="admin-inquiries-list" className="mt-6 space-y-4">
          {filtered.map((inq) => {
            const meta = INQUIRY_STATUS_META[inq.status || "new"];
            const thread = inq.thread || [];
            const threadOpen = openThreads.has(inq.id);
            return (
              <li key={inq.id} data-testid={`admin-inquiry-${inq.id}`} className="rounded-2xl border border-white/[0.08] bg-card p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-display text-base font-extrabold text-paper">{inq.name}</p>
                    <p className="mt-0.5 text-sm text-paper/55">{inq.email}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <span className="inline-block rounded-full bg-lilac/15 px-3 py-1 text-xs font-bold text-lilac">{inq.need}</span>
                    <span
                      data-testid={`inquiry-status-${inq.id}`}
                      className="rounded-full px-3 py-1 text-xs font-bold"
                      style={{ backgroundColor: `${meta.color}1f`, color: meta.color }}
                    >
                      {meta.label}
                    </span>
                  </div>
                </div>
                {inq.idea && <p className="mt-4 border-t border-white/[0.06] pt-4 text-sm text-paper/65">{inq.idea}</p>}
                {(() => {
                  const a = inq.attribution || {};
                  const source = a.utm_source || (a.referrer ? a.referrer.replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0] : "") || "direct";
                  const bits = [source];
                  if (a.utm_campaign) bits.push(a.utm_campaign);
                  if (a.landing_page) bits.push(`landed ${a.landing_page}`);
                  return (
                    <p data-testid={`inquiry-source-${inq.id}`} className="mt-3 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-paper/35">
                      <Globe className="h-3 w-3" aria-hidden="true" />
                      Source: {bits.join(" · ")}
                    </p>
                  );
                })()}
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-white/[0.06] pt-4">
                  <div className="flex items-center gap-3">
                    <p className="text-xs text-paper/40">
                      {new Date(inq.created_at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
                    </p>
                    <button
                      type="button"
                      data-testid={`inquiry-thread-toggle-${inq.id}`}
                      aria-expanded={threadOpen}
                      onClick={() => toggleThread(inq.id)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs font-bold text-paper/55 transition-colors duration-200 hover:border-lilac/50 hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac"
                    >
                      <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
                      Conversation ({thread.length})
                    </button>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <select
                      data-testid={`inquiry-status-select-${inq.id}`}
                      value={inq.status || "new"}
                      onChange={(e) => setStatus(inq, e.target.value)}
                      aria-label={`Pipeline status for ${inq.name}`}
                      className="rounded-full border border-white/10 bg-ink/70 px-3 py-1.5 text-xs font-bold text-paper/70 focus:border-lilac/60 focus:outline-none"
                    >
                      {Object.entries(INQUIRY_STATUS_META).map(([key, m]) => (
                        <option key={key} value={key}>{m.label}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      data-testid={`inquiry-followup-${inq.id}`}
                      onClick={() => onFollowUp({ to_email: inq.email, to_name: inq.name, inquiry_id: inq.id })}
                      className="inline-flex items-center gap-1.5 rounded-full bg-lilac px-4 py-1.5 text-xs font-bold text-ink transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-lilac-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac"
                    >
                      <Mail className="h-3.5 w-3.5" aria-hidden="true" />
                      Follow up
                    </button>
                  </div>
                </div>
                {threadOpen && (
                  <div data-testid={`inquiry-thread-${inq.id}`} className="mt-4 rounded-xl border border-white/[0.07] bg-ink/50 p-4">
                    {thread.length === 0 ? (
                      <p className="text-xs text-paper/40">No follow-ups sent to this person yet.</p>
                    ) : (
                      <ul className="space-y-2.5">
                        {thread.map((s) => (
                          <li key={s.id} data-testid={`thread-send-${s.id}`} className="rounded-lg bg-white/[0.03] px-3.5 py-2.5">
                            {s.kind === "reply" ? (
                              <div>
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <div className="flex min-w-0 items-center gap-2">
                                    <span className="shrink-0 rounded-full bg-[#8FD8B8]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#8FD8B8]">
                                      Reply
                                    </span>
                                    <span className="truncate text-xs text-paper/40">from {s.from_email}</span>
                                  </div>
                                  <span className="shrink-0 text-xs text-paper/35">
                                    {new Date(s.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                                  </span>
                                </div>
                                <p className="mt-1.5 whitespace-pre-line text-xs leading-relaxed text-paper/65">{s.note}</p>
                              </div>
                            ) : (
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="flex min-w-0 items-center gap-2">
                                  {s.auto ? (
                                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-lilac/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-lilac">
                                      <Zap className="h-3 w-3" aria-hidden="true" />
                                      Auto
                                    </span>
                                  ) : (
                                    <span className="shrink-0 rounded-full border border-white/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-paper/40">
                                      Manual
                                    </span>
                                  )}
                                  <span className="truncate text-xs font-bold text-paper/75">{s.template_name}</span>
                                  <span className="truncate text-xs text-paper/40">→ {s.to_email}</span>
                                </div>
                                <span className="shrink-0 text-xs text-paper/35">
                                  {new Date(s.sent_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                                </span>
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="mt-3 border-t border-white/[0.06] pt-3">
                      <label htmlFor={`reply-note-${inq.id}`} className="sr-only">
                        Log a reply from {inq.name}
                      </label>
                      <textarea
                        id={`reply-note-${inq.id}`}
                        data-testid={`reply-note-${inq.id}`}
                        rows={2}
                        maxLength={4000}
                        value={replyDrafts[inq.id] || ""}
                        onChange={(e) => setReplyDrafts((d) => ({ ...d, [inq.id]: e.target.value }))}
                        placeholder={`Log a reply from ${inq.name || "this person"} — paste the key points of their email here.`}
                        className="w-full rounded-lg border border-white/10 bg-ink/70 px-3 py-2 text-xs text-paper placeholder:text-paper/30 focus:border-lilac/60 focus:outline-none focus:ring-1 focus:ring-lilac/30"
                      />
                      <button
                        type="button"
                        data-testid={`reply-save-${inq.id}`}
                        disabled={replyBusy === inq.id || !(replyDrafts[inq.id] || "").trim()}
                        onClick={() => logReply(inq)}
                        className="mt-2 rounded-full border border-[#8FD8B8]/40 bg-[#8FD8B8]/10 px-4 py-1.5 text-xs font-bold text-[#8FD8B8] transition-colors duration-200 hover:bg-[#8FD8B8]/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {replyBusy === inq.id ? "Saving…" : "Log reply"}
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
